import { reactive, watch } from 'vue';

/**
 * Editor preferences — small, localStorage-backed, surface-agnostic. This is
 * the shared settings slice the Settings surface edits and other surfaces read.
 * Grows as new homebrew editors land (e.g. default grid snap, unit defaults).
 */
export interface EditorSettings {
  /** Auto-select an element on the map right after it is placed. */
  autoSelectPlaced: boolean;
  /** Share one mirror default across the objective and terrain tools. */
  toolsShareMirror: boolean;
  /** Show each entity's id (editable, regenerable) in the authoring forms. */
  showIds: boolean;
}

const KEY = 'studio:settings';

const defaults: EditorSettings = {
  autoSelectPlaced: true,
  toolsShareMirror: false,
  showIds: false,
};

function load(): EditorSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...defaults };
    return { ...defaults, ...(JSON.parse(raw) as Partial<EditorSettings>) };
  } catch {
    return { ...defaults };
  }
}

const settings = reactive<EditorSettings>(load());

let watching = false;

export function useSettings() {
  if (!watching) {
    watching = true;
    watch(settings, (value) => localStorage.setItem(KEY, JSON.stringify(value)), { deep: true });
  }
  return settings;
}
